// client/src/useCurrentPBVEvent.ts
import { useMemo } from "react";
import type { PBVInsights, PBVRally, PBVShot } from "./pbvTypes";

type UseCurrentPBVEventArgs = {
  currentTimeSec: number;
  insights: PBVInsights | null;
};

type UseCurrentPBVEventResult = {
  currentMs: number;
  currentRally: PBVRally | null;
  currentShot: PBVShot | null;
};

/**
 * Normalize shot timing so we work with both:
 * - NEW: shot.start_ms / shot.end_ms
 * - OLD: shot.resulting_ball_movement.trajectory.start.ms / end.ms
 */
export function getShotTimesMs(shot: PBVShot): { startMs: number | null; endMs: number | null } {
  let startMs: number | null = (shot as any).start_ms ?? null;
  let endMs: number | null = (shot as any).end_ms ?? null;

  const traj = shot.resulting_ball_movement?.trajectory;
  if ((startMs == null || endMs == null) && traj) {
    if (startMs == null && typeof traj.start?.ms === "number") {
      startMs = traj.start.ms;
    }
    if (endMs == null && typeof traj.end?.ms === "number") {
      endMs = traj.end.ms;
    }
  }

  return { startMs, endMs };
}

export function useCurrentPBVEvent({
  currentTimeSec,
  insights,
}: UseCurrentPBVEventArgs): UseCurrentPBVEventResult {
  return useMemo(() => {
    const currentMs = Math.round(currentTimeSec * 1000);

    if (!insights || !Array.isArray(insights.rallies)) {
      return { currentMs, currentRally: null, currentShot: null };
    }

    let currentRally: PBVRally | null = null;
    let currentShot: PBVShot | null = null;

    outer: for (const rally of insights.rallies as PBVRally[]) {
      const shots: PBVShot[] = (rally as any).shots ?? [];
      for (const shot of shots) {
        const { startMs, endMs } = getShotTimesMs(shot);
        if (startMs == null || endMs == null) continue;

        if (currentMs >= startMs && currentMs <= endMs) {
          currentRally = rally;
          currentShot = shot;
          break outer;
        }
      }
    }

    return { currentMs, currentRally, currentShot };
  }, [currentTimeSec, insights]);
}

export function useCurrentPBVEvent_old({
  currentTimeSec,
  insights,
}: UseCurrentPBVEventArgs): UseCurrentPBVEventResult {
  const currentMs = Math.max(0, Math.round(currentTimeSec * 1000));

  return useMemo(() => {
    if (!insights) {
      return {
        currentMs,
        currentRally: null,
        currentShot: null,
      };
    }

    const rallies = insights.rallies ?? [];
    let foundRally: PBVRally | null = null;
    let foundShot: PBVShot | null = null;

    for (const rally of rallies) {
      if (currentMs < rally.start_ms || currentMs >= rally.end_ms) {
        continue;
      }
      foundRally = rally;

      // Find shot inside this rally
      for (const shot of rally.shots ?? []) {
        if (currentMs >= shot.start_ms && currentMs < shot.end_ms) {
          foundShot = shot;
          break;
        }
      }

      break;
    }

    return {
      currentMs,
      currentRally: foundRally,
      currentShot: foundShot,
    };
  }, [currentMs, insights]);
}
